// Column edit planners for the CREATE TABLE source path. Each op produces a
// single PlannedByteEdit that splices the column list inline:
//   - add-column → insert a column body (and the dominant list separator) at
//     the position implied by `afterColumn` (or appended).
//   - drop-column → delete the column segment plus the adjacent comma.
//   - rename-column → replace just the name range inside the column segment.
//
// The ALTER TABLE source path lives in `columnEdit.alterTable.ts`. The
// dispatcher (`columnEdit.ts`) routes by `DdlStructure.kind`.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Column Edit Applicator.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { IdentifierQuoting } from '../../../sql-grammar/model';
import type { ColumnDef, DdlStructure, PlannedByteEdit } from '../../types';
import { DdlEditError } from '../../types';
import { readIdentifier } from '../../scope/identifiers';

import type { DdlEditCtx } from './applyDdlEdit';
import { formatColumnBody } from './dialect';
import { type ExistingNameOrigin, quoteWithStyle } from './dialect.quoting';
import { readTableHeader } from '../extract/createTable';
import { findMatchingParen, skipWsCommentsLocal } from '../extract/shared';

type CreateTableStruct = Extract<DdlStructure, { kind: 'create-table' }>;

/** New Table starts with an explicit empty list; CTAS/LIKE must stay refused. */
export function planCreateFirstColumn(ctx: DdlEditCtx, s: CreateTableStruct, column: ColumnDef): PlannedByteEdit {
  const { startOffset, endOffset } = ctx.statementInfo.range;
  const header = readTableHeader(ctx.source, ctx.options, startOffset, endOffset);
  const open = header ? skipWsCommentsLocal(ctx.source, header.afterName, endOffset, ctx.options) : -1;
  const close = open >= 0 && ctx.source[open] === '('
    ? findMatchingParen(ctx.source, ctx.options, open, endOffset) : -1;
  if (close < 0 || skipWsCommentsLocal(ctx.source, open + 1, close, ctx.options) !== close) {
    throw new Error('applyDdlEdit: adding the first column requires an explicit empty CREATE TABLE column list');
  }
  return {
    range: rangeFromOffsets(ctx.lineIdx, close, close),
    replacement: `\n  ${formatColumnBody(ctx.engine, column, s.table.quoted, s.table)}\n`,
    rationale: 'add-column:create-table',
  };
}

export function planCreateAdd(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  column: ColumnDef,
  afterColumn: string | undefined,
): PlannedByteEdit {
  const cols = s.columns;
  const idx = afterColumn === undefined
    ? cols.length - 1
    : cols.findIndex((c) => c.name === afterColumn);
  if (afterColumn !== undefined && idx < 0) {
    throw new Error(`applyDdlEdit: afterColumn '${afterColumn}' not found in column list`);
  }
  const anchor = cols[idx];
  if (!anchor.range) {
    throw new Error(`applyDdlEdit: column '${anchor.name}' has no source range`);
  }
  const sep = detectListSeparator(ctx.source, cols);
  const body = formatColumnBody(ctx.engine, column, s.table.quoted, s.table);
  const insertAt = anchor.range.endOffset;
  return {
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + body,
    rationale: 'add-column:create-table',
  };
}

export function planCreateDrop(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  columnName: string,
  origin: ExistingNameOrigin | undefined,
): PlannedByteEdit {
  const cols = s.columns;
  const idx = declaredColumnIndex(cols, columnName, origin);
  if (idx < 0) {
    throw new Error(`applyDdlEdit: column '${columnName}' not found in CREATE TABLE`);
  }
  if (cols.length === 1) {
    throw new Error(
      `applyDdlEdit: cannot drop the only column from CREATE TABLE `
        + `(would produce an invalid empty column list)`,
    );
  }
  const a = cols[idx].range!;
  const start = idx === 0 ? a.startOffset : cols[idx - 1].range!.endOffset;
  const end = idx === 0 ? cols[1].range!.startOffset : a.endOffset;
  return {
    range: rangeFromOffsets(ctx.lineIdx, start, end),
    replacement: '',
    rationale: 'drop-column:create-table',
  };
}

export function planCreateRename(
  ctx: DdlEditCtx,
  s: CreateTableStruct,
  oldName: string,
  newName: string,
  origin: ExistingNameOrigin | undefined,
): PlannedByteEdit {
  const cols = s.columns;
  const idx = declaredColumnIndex(cols, oldName, origin);
  if (idx < 0) {
    throw new Error(`applyDdlEdit: column '${oldName}' not found in CREATE TABLE`);
  }
  const seg = cols[idx].range!;
  const ident = readIdentifier(ctx.source, seg.startOffset, seg.endOffset, ctx.options);
  if (!ident) {
    throw new Error(`applyDdlEdit: failed to locate name range for column '${oldName}'`);
  }
  // v2 Slice H2: preserve the source token's per-token quoting style
  // (`[Foo]` vs `"Foo"` vs unquoted) — `ident.quoting` carries the open-quote
  // char the lexer saw at this exact position.
  const replacement = quoteWithStyle(newName, ident.quoting);
  return {
    range: rangeFromOffsets(ctx.lineIdx, ident.start, ident.end),
    replacement,
    rationale: 'rename-column:create-table',
  };
}

/** WHICH declared column an edit's name picks, or `-1` when the list has none.
 *
 *  A name is not an identity when the list holds it twice with different
 *  delimiters: `CREATE TABLE t (Id integer, "Id" integer)` declares `id` and
 *  `Id` on PostgreSQL, two columns that a `findIndex` on the stripped bytes
 *  silently resolved to whichever came FIRST. The caller's `origin` is the one
 *  fact that separates them (`dialect.quoting.ts`'s {@link ExistingNameOrigin}
 *  — `'bare-reference'` for the spelling the source wrote bare, the other two
 *  arms for the one it wrote delimited), and without it an ambiguous request
 *  is REFUSED rather than guessed — the same ruling
 *  `columnEdit.alterTable.ts`'s `sourceSpellingOrigin` makes for the emitted
 *  ALTER path, reached here for the in-place byte edit. */
function declaredColumnIndex(
  cols: readonly ColumnDef[],
  name: string,
  origin: ExistingNameOrigin | undefined,
): number {
  const matches = cols
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c.name === name);
  // PRECEDENCE, and it differs from the ALTER path ON PURPOSE. Here an
  // explicit origin is a TIEBREAKER — consulted only when the name is
  // declared more than once — whereas `columnEdit.alterTable.ts` lets it
  // OVERRIDE the source's own evidence. The asymmetry is not an oversight:
  // a CREATE TABLE holds every declaration of the name, so with one match
  // the structure already KNOWS which column is meant and a contradicting
  // origin would be the caller mis-describing a column the statement can
  // see. An ALTER's clauses are evidence about a table the statement does
  // not contain, so a caller that says which occurrence it means is adding
  // information, not contradicting it. On a single declaration both policies
  // emit the same clause; on a duplicate pair each does the right thing for
  // its own source kind, so converging them would mean weakening one.
  if (matches.length <= 1) return matches[0]?.i ?? -1;
  const wantDelimited = origin !== undefined && origin !== 'bare-reference';
  const byOrigin = origin === undefined
    ? []
    : matches.filter(({ c }) => isDelimited(c.nameQuoting) === wantDelimited);
  if (byOrigin.length === 1) return byOrigin[0].i;
  throw new DdlEditError(
    'invalid-edit',
    `applyDdlEdit: '${name}' is ambiguous — this CREATE TABLE declares it `
      + `${matches.length} times and a name alone cannot say which column the `
      + `edit means. Pass 'columnNameOrigin' ('oldNameOrigin' on a rename): `
      + `'delimited-source' for the column the source wrote in delimiters, `
      + `'bare-reference' for the one it wrote bare.`,
  );
}

function isDelimited(quoting: IdentifierQuoting | undefined): boolean {
  return quoting !== undefined && quoting !== 'unquoted';
}

/** Determine the dominant column-list separator by a MAJORITY VOTE over the
 *  verbatim inter-column slices of every adjacent pair. The most frequent
 *  slice wins; ties resolve to the first-occurring style. Uniform-preserving
 *  (a single consistent separator yields that exact slice, unchanged from the
 *  old last-two sampling) while mixed lists no longer lose fidelity. Falls
 *  back to `, ` for <2 columns. Mirrors the SR-Edit `detectListSeparator`
 *  posture (kept as an independent function per the two-file split). */
function detectListSeparator(source: string, cols: readonly ColumnDef[]): string {
  if (cols.length < 2) return ', ';
  const counts = new Map<string, number>();
  // cols.length >= 2 guarantees ≥1 iteration, and the first iteration always
  // satisfies `n (=1) > bestCount (=0)`, so `best` is set before it is read —
  // no first-slice seeding needed (mirrors the SR-Edit `majoritySlice` shape).
  let best = '';
  let bestCount = 0;
  for (let k = 0; k < cols.length - 1; k++) {
    const slice = source.slice(cols[k].range!.endOffset, cols[k + 1].range!.startOffset);
    const n = (counts.get(slice) ?? 0) + 1;
    counts.set(slice, n);
    if (n > bestCount) { bestCount = n; best = slice; }
  }
  return best;
}
